import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewChecked,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApplicationDataService } from './../../core/services/application-data.service';
import { InternalChatbotService } from './../../core/services/internal-chatbot.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateOncePipe } from './translate-once.pipe';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { AuthenticationService } from './../../core/services/authentication.service';
import { TokenStorageService } from './../../core/services/token-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { VoiceInputComponent } from './voice-input/voice-input.component';
import { COMPLAINT_STEPS, CHATBOT_TRACKING_ID_PATTERN } from './chatbot.constants';
import { ChatMode, FormFieldUpdateEvent, Message } from './chatbot.models';
import { extractBotResponse, formatTimer, parseBotResponse } from './chatbot.utils';
import { SessionService } from '../../core/services/session.service';
import { ChatbotBridgeService } from '../../core/services/chatbot-bridge.service';
import { InternalChatApplication } from '../../core/models/internal-chatbot.model';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
  imports: [CommonModule, FormsModule, TranslateModule, VoiceInputComponent, TranslateOncePipe],
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateY(0)', opacity: 1 })),
      state('out', style({ transform: 'translateY(20px)', opacity: 0 })),
      transition('out => in', animate('300ms ease-in')),
      transition('in => out', animate('300ms ease-out'))
    ]),
    trigger('fabRotate', [
      state('closed', style({ transform: 'rotate(0deg)' })),
      state('open', style({ transform: 'rotate(45deg)' })),
      transition('closed => open', animate('300ms ease-in-out')),
      transition('open => closed', animate('300ms ease-in-out'))
    ])
  ]
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('voiceInputRef') private voiceInputRef!: VoiceInputComponent;
  @Output() formFieldUpdate = new EventEmitter<FormFieldUpdateEvent>();


  private destroy$ = new Subject<void>();
  private typingSpeed = 30;

  isOpen = false;
  messages: Message[] = [];
  currentMessage = '';
  isTyping = false;
  isSending = false;
  private welcomePending = false;

  enabledApplications: InternalChatApplication[] = [];
  selectedApplicationCode = 'GRMIS';
  selectedHermesMode: 'OBSERVER' | 'ANALYST' | 'ADVISOR' | 'OPERATOR' | 'AUTONOMOUS' = 'OBSERVER';
  private applicationSessions = new Map<string, string>();
  private applicationMessages = new Map<string, Message[]>();




  sessionId: string = '';
  currentPage: string = '';
  currentDir: 'ltr' | 'rtl' = 'ltr';

  // Chat modes: 'general' | 'guided' | 'status' | 'otp_input_wait' | 'otp_expired'
  chatMode: ChatMode = 'general';

  // Status Check Properties
  currentComplaintNum: string = '';
  otpTimer: number = 0;
  otpTimerSubscription: any;
  otpMessageId: string = '';
  timeLeft: number = 0;
  timerIntervalId: any = null;

  // Guided form filling properties
  isGuidedMode = false;
  currentStep = 0;
  formData: any = {};
  complaintSteps = COMPLAINT_STEPS;

  // Dynamic data lists
  categoryList: any[] = [];
  subCategoryList: any[] = [];
  divisionList: any[] = [];
  districtList: any[] = [];
  tehsilList: any[] = [];
  mouzaList: any[] = [];
  divisionIds: any[] = [];

  // Document upload properties
  showDocumentUpload = false;
  documentList: any[] = [];
  fileError = false;
  showSubmitButton = false;

  // Custom dropdown state
  dropdownOpen: string | null = null;
  selectedDropdownValues: Map<string, any> = new Map();
  dropdownSearchQuery: string = '';

  // Loading flags to prevent duplicate API calls
  private loadingFlags = {
    categories: false,
    subcategories: false,
    divisions: false,
    districts: false,
    tehsils: false,
    mouzas: false
  };

  // Cache to prevent duplicate API calls
  private dataCache = {
    categories: null as any[] | null,
    subcategories: new Map<number, any[]>(),
    divisions: null as any[] | null,
    districts: new Map<number, any[]>(),
    tehsils: new Map<number, any[]>(),
    mouzas: new Map<number, any[]>()
  };

  // Debounce timers to prevent rapid API calls
  private debounceTimers = {
    categories: null as any,
    subcategories: null as any,
    divisions: null as any,
    districts: null as any,
    tehsils: null as any,
    mouzas: null as any
  };

  // Track active API calls to prevent duplicates
  private activeApiCalls = new Set<string>();

  private pendingAutoStart = false;

  constructor(
    private applicationDataService: ApplicationDataService,
    private internalChatbotService: InternalChatbotService,
    private sessionService: SessionService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private tokenStorage: TokenStorageService,
    private translate: TranslateService,
    private chatbotBridge: ChatbotBridgeService
  ) {
    this.divisionIds = this.tokenStorage.getDivisions();
  }

  ngOnInit() {
    this.chatbotBridge.registerChatbot(this);
    this.sessionId = this.sessionService.getSessionId();
    this.loadEnabledChatApplications();

    // Read and immediately consume the guided-form flag synchronously at startup.
    // This handles the case where the user clicked "Register a Complaint" from
    // another page and was redirected here — the new chatbot instance picks up the flag.
    const shouldAutoStartGuided = sessionStorage.getItem('autoStartGuided') === '1';
    if (shouldAutoStartGuided) {
      sessionStorage.removeItem('autoStartGuided');
    }

    // Set initial direction based on current language
    this.currentDir = this.translate.currentLang === 'ur' ? 'rtl' : 'ltr';

    // Subscribe to language changes for RTL/LTR direction
    this.translate.onLangChange.subscribe((event) => {
      this.currentDir = event.lang === 'ur' ? 'rtl' : 'ltr';
    });

    // Subscribe to router navigation end events to update currentPage
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const oldPage = this.currentPage;
      this.currentPage = this.getCurrentPageName();

      // Start guided form when navigation to complaint/add completes (triggered by chatbot button)
      if (this.pendingAutoStart && this.currentPage === 'complaint/add') {
        this.pendingAutoStart = false;
        setTimeout(() => {
          if (!this.isOpen) this.openChat();
          this.chatMode = 'guided';
          this.startGuidedForm();
        }, 500);
        return;
      }

      // If page changed and we are in general mode, just update existing menu buttons
      if (oldPage !== this.currentPage && this.chatMode === 'general' && !this.isGuidedMode) {
        this.updateMainMenuButtons();
        // Auto-open chatbot when navigating to complaint page
        this.autoOpenForPage(this.currentPage);
      }
    });

    // Initialize currentPage immediately on load
    this.currentPage = this.getCurrentPageName();

    // Show initial greeting and options AFTER translation is ready
    this.translate.get('CHATBOT_WELCOME').subscribe(() => {
      if (this.currentPage !== 'complaint/add') {
        this.showMainMenu();
      } else {
        this.resetChatStateOnly();
      }

      // Fallback: if page was loaded directly at /complaint/add with the flag set
      // (e.g. user refreshed the page), auto-start the guided form.
      if (shouldAutoStartGuided && this.currentPage === 'complaint/add') {
        setTimeout(() => {
          if (!this.isOpen) this.openChat();
          this.chatMode = 'guided';
          this.startGuidedForm();
        }, 1500);
      } else {
        this.autoOpenForPage(this.currentPage);
      }
    });
  }

  private loadEnabledChatApplications() {
    this.internalChatbotService.getEnabledApplications().subscribe({
      next: applications => {
        this.enabledApplications = applications;
        if (!applications.some(app => app.applicationCode === this.selectedApplicationCode)) {
          this.selectedApplicationCode = applications[0]?.applicationCode || '';
        }
      },
      error: () => {
        // Preserve the established GRMIS chatbot when application discovery is temporarily unavailable.
        this.enabledApplications = [{ applicationId: 0, applicationCode: 'GRMIS', applicationName: 'GRMIS' }];
        this.selectedApplicationCode = 'GRMIS';
      }
    });
  }

  onApplicationChange(applicationCode: string) {
    if (!applicationCode || applicationCode === this.selectedApplicationCode) return;
    this.applicationMessages.set(this.selectedApplicationCode, [...this.messages]);
    this.selectedApplicationCode = applicationCode;
    this.chatMode = 'general';
    this.isGuidedMode = false;
    this.messages = this.applicationMessages.get(applicationCode) || [];
    if (this.messages.length === 0) {
      if (applicationCode === 'GRMIS') this.showMainMenu();
      else this.messages.push({
        text: applicationCode === 'NMS'
          ? `Hello, I'm Saba, your NMS network monitoring assistant. Ask me about monitored devices or their status.`
          : `You are now chatting with ${applicationCode}.`,
        sender: 'bot', timestamp: new Date(), id: this.generateId()
      });
    }
    this.scrollToBottom();
  }



  private getCurrentPageName(): string {
    const segments: string[] = [];
    let route = this.activatedRoute.root;
    while (route.firstChild) {
      route = route.firstChild;
      route.snapshot.url.forEach(seg => segments.push(seg.path));
    }
    return segments.join('/') || 'index';
  }

  onInput(event: any) {
    this.autoResize();
  }

  autoResize() {
    const textarea = document.querySelector('.chat-textarea') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }

  ngAfterViewChecked() {
    // Removed auto-scroll to allow user to scroll up manually.
    // Scroll behavior is now handled by explicit scrollToBottom() calls.
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear all debounce timers
    Object.values(this.debounceTimers).forEach(timer => {
      if (timer) clearTimeout(timer);
    });

    // Clear active API calls
    this.activeApiCalls.clear();
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  closeChat() {
    this.isOpen = false;
  }

  openChat() {
    this.isOpen = true;
  }

  // Method to auto-open chatbot for specific pages
  autoOpenForPage(pageName: string) {
    if (pageName === 'complaint/add') {
      setTimeout(() => {
        if (!this.isOpen) {
          this.openChat();

          // On complaint page, if chat is newly opened and empty, show only the assist prompt
          if (this.messages.length === 0) {
            this.isTyping = true;
            this.scrollToBottom();

            setTimeout(() => {
              this.isTyping = false;
              this.messages.push({
                text: 'CHATBOT_HELP_REGISTER_PROMPT',
                sender: 'bot',
                timestamp: new Date(),
                id: this.generateId(),
                showButtons: true,
                buttons: [
                  { text: 'CHATBOT_YES', action: 'confirm_start_guided' },
                  { text: 'CHATBOT_NO', action: 'decline_assist' }
                ]
              });
              this.scrollToBottom();
            }, 1000);
          }
        }
      }, 1500); // Open after 1.5 seconds
    }
  }

  onVoiceText(text: string) {
    if (this.isSending) return;
    this.currentMessage = text;
    setTimeout(() => this.autoResize(), 0);
  }

  onInterimVoiceText(text: string) {
    if (this.isSending) return;
    this.currentMessage = text;
    setTimeout(() => this.autoResize(), 0);
  }

  sendMessage() {
    // Stop mic silently if recording when user hits send
    this.voiceInputRef?.forceStop();

    const text = this.currentMessage.trim();
    if (!text || this.isSending) return;

    // Hide all previous options and buttons when user types manually
    this.messages.forEach(msg => {
      msg.showButtons = false;
      msg.showOptions = false;
    });

    // Check if we're in guided mode and expecting text input
    if (this.isGuidedMode && this.currentStep < this.complaintSteps.length) {
      const currentStepData = this.complaintSteps[this.currentStep];
      if (currentStepData.type === 'text') {
        this.onTextSubmit(currentStepData.field, text);
        this.currentMessage = '';
        setTimeout(() => this.autoResize(), 0);
        return;
      } else {
        // If in guided mode but not a text field (e.g., dropdown shown), 
        // clear input and don't make API calls
        this.currentMessage = '';
        setTimeout(() => this.autoResize(), 0);
        return;
      }
    }

    // Check if we're in status mode
    if (this.chatMode === 'status') {
      this.messages.push({
        text,
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.currentMessage = '';
      setTimeout(() => this.autoResize(), 0);
      this.handleStatusCheck(text);
      return;
    }

    // Check if we're waiting for OTP
    if (this.chatMode === 'otp_input_wait') {
      this.messages.push({
        text,
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.currentMessage = '';
      setTimeout(() => this.autoResize(), 0);
      this.verifyOTP(text);
      return;
    }

    // Check if OTP has expired
    if (this.chatMode === 'otp_expired') {
      this.messages.push({
        text,
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.currentMessage = '';
      setTimeout(() => this.autoResize(), 0);

      this.isTyping = true;
      this.scrollToBottom();

      setTimeout(() => {
        this.isTyping = false;
        this.messages.push({
          text: 'CHATBOT_OTP_EXPIRED',
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          showButtons: true,
          buttons: [
            { text: 'CHATBOT_RETRY_OTP', action: 'retry_otp' },
            { text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }
          ]
        });
        this.scrollToBottom();
      }, 1000);
      return;
    }

    this.messages.push({
      text,
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    this.currentMessage = '';
    setTimeout(() => {
      this.currentMessage = '';
      this.autoResize();
    });
    this.isTyping = true;
    this.isSending = true;
    this.scrollToBottom();

    this.sendInternalMessage(text);
  }

  private sendInternalMessage(question: string) {
    if (this.selectedApplicationCode && this.selectedApplicationCode !== 'GRMIS') {
      const code = this.selectedApplicationCode;
      const sessionId = this.applicationSessions.get(code) || this.sessionService.getSessionId();
      this.internalChatbotService.askApplicationQuestion({
        applicationCode: code,
        sessionId,
        message: question,
        language: this.translate.currentLang || 'en',
        hermesMode: this.selectedHermesMode
      }).subscribe({
        next: response => {
          this.applicationSessions.set(code, response.sessionId);
          this.isTyping = false;
          this.streamBotMessage(response.answer || 'Network monitoring information is temporarily unavailable.');
        },
        error: () => {
          this.isTyping = false;
          this.isSending = false;
          this.messages.push({ text: 'Network monitoring information is temporarily unavailable.', sender: 'bot', timestamp: new Date(), id: this.generateId() });
          this.scrollToBottom();
        }
      });
      return;
    }
    const request = {
      sessionId: this.tokenStorage.getEmail() || this.sessionService.getSessionId(),
      userId: this.tokenStorage.getUserId() || undefined,
      userQuestion: question
    };

    this.internalChatbotService.askCitizenQuestion(request).subscribe({
      next: (response) => {
        setTimeout(() => {
          this.isTyping = false;
          const reply = (response.matchedAnswer || 'No relevant information found in the documents.').trim();
          this.streamBotMessage(reply);
          this.isSending = false;
        }, 800);
      },
      error: () => {
        setTimeout(() => {
          this.isTyping = false;
          this.isSending = false;
          this.messages.push({
            text: 'Failed to get a response. Please try again.',
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId()
          });
          this.scrollToBottom();
        }, 800);
      }
    });
  }

  private streamBotMessage(fullText: string, html?: SafeHtml) {
    const messageId = this.generateId();

    // Always add Back to Main Menu button
    const buttons = [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }];

    const botMessage: Message = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      id: messageId,
      isStreaming: true,
      html,
      showButtons: true,
      buttons: buttons
    };

    if (html) {
      // If we have HTML content, show it immediately without streaming text
      this.messages.push(botMessage);
      this.isSending = false;
    } else {
      // Only stream text if there is no HTML content
      this.messages.push(botMessage);

      let currentIndex = 0;
      const messageIndex = this.messages.length - 1;

      // Dynamic speed for bot responses to prevent long delays for long text
      const maxTotalDuration = 2000; // ms
      let effectiveSpeed = this.typingSpeed;
      if (fullText.length * this.typingSpeed > maxTotalDuration) {
        effectiveSpeed = Math.floor(maxTotalDuration / fullText.length);
        if (effectiveSpeed < 5) effectiveSpeed = 5;
      }

      const typingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          this.messages[messageIndex].text += fullText[currentIndex];
          currentIndex++;
          // Only scroll during streaming if no options are active
          if (!this.hasActiveOptions()) {
            setTimeout(() => this.scrollToBottom(), 10);
          }
        } else {
          clearInterval(typingInterval);
          this.messages[messageIndex].isStreaming = false;
          this.isSending = false;
        }
      }, effectiveSpeed);

      this.destroy$.subscribe(() => clearInterval(typingInterval));
    }
  }



  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom() {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;

      // Allow Angular a tick to render
      setTimeout(() => {
        if (this.isTyping) {
          const typingEl = document.getElementById('chatbot-typing-indicator');
          if (typingEl) {
            el.scrollTo({ top: typingEl.offsetTop - 20, behavior: 'smooth' });
            return;
          }
        }

        if (this.messages.length > 0) {
          const lastMessage = this.messages[this.messages.length - 1];
          const lastMessageEl = document.getElementById('message-' + lastMessage.id);
          if (lastMessageEl) {
            el.scrollTo({ top: lastMessageEl.offsetTop - 20, behavior: 'smooth' });
            return;
          }
        }

        el.scrollTop = el.scrollHeight;
      }, 50);
    }
  }

  isInputDisabled(): boolean {
    if (this.isTyping || this.isSending) return true;

    if (this.isGuidedMode) {
      if (this.currentStep < this.complaintSteps.length) {
        return this.complaintSteps[this.currentStep].type !== 'text';
      }
      return true;
    }

    return false;
  }

  isSendButtonDisabled(): boolean {
    if (this.isInputDisabled() || !this.currentMessage?.trim()) return true;

    if (this.isGuidedMode) {
      const maxLength = this.getCurrentMaxLength();
      if (maxLength && this.currentMessage.length > maxLength) {
        return true;
      }
    }

    return false;
  }

  getCurrentMaxLength(): number | undefined {
    if (this.isGuidedMode && this.currentStep < this.complaintSteps.length) {
      return this.complaintSteps[this.currentStep].maxLength;
    }
    return undefined;
  }

  getInputPlaceholder(): string {
    if (this.isGuidedMode) {
      if (this.currentStep < this.complaintSteps.length) {
        const step = this.complaintSteps[this.currentStep];
        // Note: For complex placeholders like "Please enter [label]", we might need a more complex key or handle it in HTML
        // For now, let's return a simple generic prompt or just the step label key to keep it manageable
        return step.label;
      }
      return 'CHATBOT_FORM_COMPLETED';
    }

    if (this.chatMode === 'status') {
      return 'CHATBOT_ENTER_COMPLAINT_NO';
    }

    if (this.chatMode === 'otp_input_wait') {
      return 'CHATBOT_ENTER_OTP';
    }

    if (this.chatMode === 'otp_expired') {
      return 'CHATBOT_TIMER_EXPIRED';
    }

    return 'CHATBOT_TYPE_MESSAGE';
  }

  private hasActiveOptions(): boolean {
    // Check if the last few messages contain active options that user might be interacting with
    const recentMessages = this.messages.slice(-3); // Check last 3 messages
    return recentMessages.some(message => message.showOptions && message.options && message.options.length > 0);
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }

  setTypingSpeed(speed: 'slow' | 'medium' | 'fast') {
    switch (speed) {
      case 'slow':
        this.typingSpeed = 50;
        break;
      case 'medium':
        this.typingSpeed = 30;
        break;
      case 'fast':
        this.typingSpeed = 15;
        break;
    }
  }

  // Handle button clicks
  onButtonClick(action: string, message?: Message) {
    // Hide buttons for the clicked message to prevent re-selection
    if (message) {
      message.showButtons = false;
    }

    if (action === 'start_guided_form') {
      this.messages.push({
        text: 'CHATBOT_REGISTER_COMPLAINT',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });

      // Skip the redundant confirmation prompt and start directly
      this.chatMode = 'guided';
      this.currentMessage = '';

      // If not on the complaint/add page, navigate there; the router event will start the form
      if (this.currentPage !== 'complaint/add') {
        this.pendingAutoStart = true;
        this.router.navigate(['/complaint/add']);
      } else {
        this.startGuidedForm();
      }
    } else if (action === 'confirm_start_guided') {
      this.chatMode = 'guided';
      this.currentMessage = '';

      this.startGuidedForm();
    } else if (action === 'get_complaint_status') {
      this.chatMode = 'status';
      this.messages.push({
        text: 'CHATBOT_GET_STATUS',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });

      this.handleGetComplaintStatus();
    } else if (action === 'question_answer') {
      this.chatMode = 'general';
      this.messages.push({
        text: 'CHATBOT_ASK_QUESTION',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });

      this.handleQuestionAnswer();
    } else if (action === 'back_to_main') {
      this.messages.push({
        text: 'CHATBOT_BACK_TO_MAIN',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.showMainMenu(true);
    } else if (action === 'retry_otp') {
      // Retry sending OTP using the same complaint number
      this.messages.push({
        text: 'CHATBOT_RETRY_OTP',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });

      this.handleStatusCheck(this.currentComplaintNum);
    } else if (action === 'decline_assist') {
      this.messages.push({
        text: 'CHATBOT_NO',
        sender: 'user',
        timestamp: new Date(),
        id: this.generateId()
      });

      // Show polite response WITH main menu options as specifically requested
      let buttons = [
        { text: 'CHATBOT_ASK_QUESTION', action: 'question_answer' }
      ];
      if (this.tokenStorage.isAuthenticated()) {
        buttons.unshift({ text: 'CHATBOT_REGISTER_COMPLAINT', action: 'start_guided_form' });
      }

      this.isTyping = true;
      this.scrollToBottom();

      setTimeout(() => {
        this.isTyping = false;
        this.messages.push({
          text: 'CHATBOT_NO_ASSIST_RESPONSE',
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          showButtons: true,
          buttons: buttons
        });

        this.scrollToBottom();
      }, 1000);
    } else if (action === 'skip_field') {
      const currentStepData = this.complaintSteps[this.currentStep];
      this.onTextSubmit(currentStepData.field, 'skip');
    }

    // Use controlled scrolling only after user interaction is complete
    /* setTimeout(() => {
    if (!this.hasActiveOptions()) {
      this.scrollToBottom();
    }
  }, 200); */
  }

  // Handle dropdown selection
  onDropdownSelect(field: string, event: any) {
    const value = event.target.value;
    if (!value) return; // If no selection made

    // Find the selected option text
    const message = this.messages.find(msg => msg.currentField === field && msg.showDropdown);
    const selectedOption = message?.dropdownOptions?.find(opt => opt.value == value);

    if (!selectedOption) return;

    // Hide the dropdown from the message
    if (message) {
      message.showDropdown = false;
      message.dropdownOptions = [];
    }

    // Add user selection message
    this.messages.push({
      text: selectedOption.text,
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Fill the form field
    this.fillFormField(field, value);

    // Move to next step
    /* setTimeout(() => {
      this.currentStep++;
      this.continueGuidedForm();
    }, 300); */
    this.currentStep++;
    this.continueGuidedForm();
  }

  // Toggle custom dropdown
  toggleDropdown(field: string) {
    if (this.dropdownOpen === field) {
      this.dropdownOpen = null;
    } else {
      this.dropdownOpen = field;
      this.dropdownSearchQuery = ''; // Reset search query when opening
    }
  }

  // Filter dropdown options based on search query
  getFilteredOptions(options: { text: string; value: any }[]): any[] {
    if (!this.dropdownSearchQuery) {
      return options;
    }
    const query = this.dropdownSearchQuery.toLowerCase();
    return options.filter(opt => opt.text.toLowerCase().includes(query));
  }

  // Select option from custom dropdown
  selectDropdownOption(field: string, option: any) {
    this.selectedDropdownValues.set(field, option);
    this.dropdownOpen = null;

    // Find and hide the dropdown from the message
    const message = this.messages.find(msg => msg.currentField === field && msg.showDropdown);
    if (message) {
      message.showDropdown = false;
      message.dropdownOptions = [];
      message.showButtons = false;
      message.buttons = [];
    }

    // Add user selection message
    this.messages.push({
      text: option.text,
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Fill the form field
    this.fillFormField(field, option.value);

    // Move to next step
    if (option.value === 'back_to_main') {
      this.showMainMenu();
      return;
    }
    this.currentStep++;
    this.isTyping = true;
    this.scrollToBottom();
    setTimeout(() => {
      this.isTyping = false;
      this.continueGuidedForm();
    }, 1000);
  }

  // Get selected option text for display
  getSelectedOptionText(field: string): string {
    const selected = this.selectedDropdownValues.get(field);
    return selected ? selected.text : '';
  }

  // Handle skip dropdown field
  onSkipDropdownField(field: string) {
    const currentStepData = this.complaintSteps[this.currentStep];

    // Explicitly clear the form field (emit null so the parent removes any pre-existing value).
    // Skip priority/severity — they have required validators + safe defaults; clearing would
    // break form validation.
    const keepDefault = field === 'priority' || field === 'severity';
    if (!keepDefault) {
      this.formFieldUpdate.emit({ field: currentStepData.field, value: null });
    }

    // Hide the dropdown from the message
    const message = this.messages.find(msg => msg.currentField === field && msg.showDropdown);
    if (message) {
      message.showDropdown = false;
      message.dropdownOptions = [];
      message.showButtons = false;
      message.buttons = [];
    }

    // Add skip message immediately
    this.messages.push({
      text: 'CHATBOT_SKIP',
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    this.isTyping = true;
    this.scrollToBottom();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        text: 'SKIPPED',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.scrollToBottom();

      // Second delay for the next question
      setTimeout(() => {
        this.isTyping = true;
        this.scrollToBottom();
        setTimeout(() => {
          this.isTyping = false;
          this.currentStep++;
          this.continueGuidedForm();
        }, 1000);
      }, 500);
    }, 1000);
  }

  // Handle option selection
  onOptionSelect(field: string, option: any) {
    // Clear options from the message that was just selected to prevent scroll blocking
    const messageWithOptions = this.messages.find(msg => msg.currentField === field && msg.showOptions);
    if (messageWithOptions) {
      messageWithOptions.showOptions = false;
      messageWithOptions.options = [];
    }

    // Handle back to main menu
    if (option.value === 'back_to_main') {
      this.showMainMenu(true);
      return;
    }

    this.formData[field] = option.value;

    this.messages.push({
      text: option.text,
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Fill the actual form field immediately without confirmation message
    this.fillFormField(field, option.value);

    // Handle document attachment selection
    if (field === 'documentAttachment') {
      if (option.value === true) {
        this.messages.push({
          text: this.translate.instant('CHATBOT_SELECT_DOCUMENT'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId()
        });
        this.showDocumentUpload = true;
        setTimeout(() => {
          const uploadContainer = document.querySelector('.document-upload-container');
          if (uploadContainer) {
            uploadContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return; // Don't move to next step yet
      } else {
        this.messages.push({
          text: this.translate.instant('CHATBOT_NO_DOCUMENT'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId()
        });
        setTimeout(() => {
          this.showSubmitStep();
        }, 500);
        return;
      }
    }

    // Handle dependent fields and reset loading flags
    if (field === 'complaintCategoryId') {
      this.subCategoryList = [];
      this.loadingFlags.subcategories = false;
      // Clear subcategory cache for this category to force fresh data
      this.formData[field] = option.value;
    } else if (field === 'divisionId') {
      this.districtList = [];
      this.tehsilList = [];
      this.mouzaList = [];
      this.loadingFlags.districts = false;
      this.loadingFlags.tehsils = false;
      this.loadingFlags.mouzas = false;
    } else if (field === 'districtId') {
      this.tehsilList = [];
      this.mouzaList = [];
      this.loadingFlags.tehsils = false;
      this.loadingFlags.mouzas = false;
    } else if (field === 'tehsilId') {
      this.mouzaList = [];
      this.loadingFlags.mouzas = false;
    }

    // Move to next step immediately
    this.currentStep++;

    // Continue with next step after a brief delay
    this.isTyping = true;
    this.scrollToBottom();
    setTimeout(() => {
      this.isTyping = false;
      this.continueGuidedForm();
    }, 1000);
  }

  // Handle text input
  onTextSubmit(field: string, text: string) {
    if (!text.trim()) return;

    const currentStepData = this.complaintSteps[this.currentStep];

    // ── Handle skip FIRST — before any validation runs ────────────────────
    // The Skip button passes the literal string 'skip'. Running validators on
    // 'skip' would incorrectly reject it (e.g. email-format check, min-length).
    if (text.trim().toLowerCase() === 'skip') {

      // Only allow skip for optional fields
      if (!currentStepData.required) {
        // Explicitly clear the form field so any pre-existing value is removed.
        // Uses the same emit path as a normal fill — parent maps and clears the control.
        this.formFieldUpdate.emit({ field: currentStepData.field, value: '' });

        this.messages.push({
          text: 'CHATBOT_SKIP',
          sender: 'user',
          timestamp: new Date(),
          id: this.generateId()
        });

        this.isTyping = true;
        this.scrollToBottom();

        setTimeout(() => {
          this.isTyping = false;
          this.messages.push({
            text: 'SKIPPED',
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId()
          });
          this.scrollToBottom();

          setTimeout(() => {
            this.isTyping = true;
            this.scrollToBottom();
            setTimeout(() => {
              this.isTyping = false;
              this.currentStep++;
              this.continueGuidedForm();
            }, 1000);
          }, 500);
        }, 1000);
        return;
      }
    }

    // ── Validate the actual input (only reached when NOT skipping) ─────────
    const validationError = this.validateFieldInput(field, text);
    if (validationError) {
      this.messages.push({ text, sender: 'user', timestamp: new Date(), id: this.generateId() });
      this.messages.push({ text: validationError, sender: 'bot', timestamp: new Date(), id: this.generateId() });
      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    if (currentStepData.maxLength && text.length > currentStepData.maxLength) {
      this.messages.push({ text, sender: 'user', timestamp: new Date(), id: this.generateId() });
      this.messages.push({
        text: this.translate.instant('CHATBOT_MAX_LENGTH_EXCEEDED', { max: currentStepData.maxLength }),
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId()
      });
      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    this.formData[field] = text;

    this.messages.push({
      text: text,
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Fill the actual form field immediately
    this.fillFormField(field, text);

    // Move to next step after a brief delay
    this.currentStep++;
    this.isTyping = true;
    this.scrollToBottom();
    setTimeout(() => {
      this.isTyping = false;
      this.continueGuidedForm();
    }, 1000);
  }

  private validateFieldInput(field: string, value: string): string | null {
    const v = value.trim();

    // ── Citizen detail fields ─────────────────────────────────────────────

    if (field === 'citizenName') {
      if (v.length < 2) return this.translate.instant('CHATBOT_INVALID_NAME');
    }

    if (field === 'citizenCNIC') {
      const digits = v.replace(/[^0-9]/g, '');
      const formatted = digits.replace(/(\d{5})(\d{7})(\d{1})/, '$1-$2-$3');
      if (digits.length !== 13 || !/^\d{5}-\d{7}-\d{1}$/.test(formatted)) {
        return this.translate.instant('CHATBOT_INVALID_CNIC');
      }
    }

    if (field === 'citizenPhone') {
      const digits = v.replace(/[^0-9]/g, '');
      if (digits.length < 10 || digits.length > 11) {
        return this.translate.instant('CHATBOT_INVALID_PHONE');
      }
    }

    // Optional — only validate if the user typed something
    if (field === 'citizenEmail' && v.length > 0) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        return this.translate.instant('CHATBOT_INVALID_EMAIL');
      }
    }

    if (field === 'citizenPassport' && v.length > 0) {
      if (v.length < 7 || v.length > 9) {
        return this.translate.instant('CHATBOT_INVALID_MIN_LENGTH', { min: 7 });
      }
    }

    // ── Complaint detail fields ───────────────────────────────────────────

    // if (field === 'complaintDetails') {
    //   if (v.length < 20) return this.translate.instant('CHATBOT_INVALID_COMPLAINT_DETAILS');
    // }

    if (field === 'complaintAddress') {
      if (v.length < 5) return this.translate.instant('CHATBOT_INVALID_ADDRESS');
    }

    return null;
  }

  private startGuidedForm() {
    // Prevent starting guided form if already in guided mode
    if (this.isGuidedMode) {
      console.log('Guided form already started, skipping duplicate start');
      return;
    }

    this.isGuidedMode = true;
    this.currentStep = 0;
    this.formData = {};

    // Reset loading flags
    this.loadingFlags = {
      categories: false,
      subcategories: false,
      divisions: false,
      districts: false,
      tehsils: false,
      mouzas: false
    };

    // Clear active API calls
    this.activeApiCalls.clear();

    // Keep cache intact to avoid duplicate API calls
    // Cache will be used if data is already available

    // Initialize data lists only if not already populated
    if (!this.categoryList.length) this.categoryList = [];
    if (!this.subCategoryList.length) this.subCategoryList = [];
    if (!this.divisionList.length) this.divisionList = [];
    if (!this.districtList.length) this.districtList = [];
    if (!this.tehsilList.length) this.tehsilList = [];
    if (!this.mouzaList.length) this.mouzaList = [];

    // Show friendly start message before jumping into questions
    this.messages.push({
      text: 'CHATBOT_GUIDED_FORM_START',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId()
    });

    setTimeout(() => {
      this.continueGuidedForm();
    }, 1000);
  }

  private continueGuidedForm() {
    if (this.currentStep >= this.complaintSteps.length) {
      this.completeGuidedForm();
      return;
    }

    const step = this.complaintSteps[this.currentStep];

    if (step.type === 'yesno') {
      this.askForYesNoSelection(step);
    } else if (step.type === 'dropdown') {
      this.askForDropdownSelection(step);
    } else if (step.type === 'text') {
      this.askForTextInput(step);
    }
  }

  private askForDropdownSelection(step: any) {
    // Show loading message with animation
    const loadingMessage: Message = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId(),
      isLoading: true,
      loadingText: 'LOADING',
      isStreaming: true
    };

    this.messages.push(loadingMessage);
    const messageIndex = this.messages.length - 1;

    // Show loading text immediately and start fetching data
    this.messages[messageIndex].text = 'LOADING';
    this.messages[messageIndex].isStreaming = false;

    // Start fetching data immediately
    setTimeout(() => {
      this.fetchDropdownData(step, messageIndex);
    }, 100);
  }

  private fetchDropdownData(step: any, loadingMessageIndex: number) {
    // Load data based on field type
    if (step.field === 'citizenGender') {
      const genderOptions = [
        { name: 'Male', value: 'Male' },
        { name: 'Female', value: 'Female' },
        { name: 'Transgender Person', value: 'Transgender Person' }
      ];
      this.showDropdownOptions(step, genderOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'segmentId') {
      const segmentOptions = [
        { name: 'PULSE', value: 1 },
        { name: 'PLRA', value: 2 }
      ];
      this.showDropdownOptions(step, segmentOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'priority') {
      const priorityOptions = [
        { name: 'Low', value: 'LOW' },
        { name: 'Medium', value: 'MEDIUM' },
        { name: 'High', value: 'HIGH' }
      ];
      this.showDropdownOptions(step, priorityOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'severity') {
      const severityOptions = [
        { name: 'Easy', value: 'EASY' },
        { name: 'Medium', value: 'MEDIUM' },
        { name: 'Complex', value: 'COMPLEX' }
      ];
      this.showDropdownOptions(step, severityOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'workCode') {
      const workCodeOptions = [
        { name: 'Department/wings as Operations', value: 'Department/wings as Operations' },
        { name: 'IT', value: 'IT' },
        { name: 'Corruption', value: 'Corruption' },
        { name: 'HR', value: 'HR' },
        { name: 'Monitoring', value: 'Monitoring' }
      ];
      this.showDropdownOptions(step, workCodeOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'complaintSource') {
      const sourceOptions = [
        { name: 'Telephone line', value: 'Telephone line' },
        { name: 'Email', value: 'Email' },
        { name: 'WhatsApp', value: 'WhatsApp' },
        { name: 'Hard Copy', value: 'Hard Copy' },
        { name: 'Other', value: 'Other' }
      ];
      this.showDropdownOptions(step, sourceOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'helplineIdentifier') {
      const helplineOptions = [
        { name: 'UAN', value: 'UAN' },
        { name: 'ARC Booth Call', value: 'ARC_Both_Call' },
        { name: 'CM Complaint Cell', value: 'CM_Complain_Cell' }
      ];
      this.showDropdownOptions(step, helplineOptions, 'name', 'value', loadingMessageIndex);
    } else if (step.field === 'complaintCategoryId') {
      this.getComplaintCategoryBySegmentAndShow(this.authService.getUserSegment(), step, loadingMessageIndex);
    } else if (step.field === 'complaintSubcategoryId') {
      const categoryId = this.formData['complaintCategoryId'];
      if (categoryId) {
        this.getAllComplaintByParentCategoryAndShow(categoryId, step, loadingMessageIndex);
      }
    } else if (step.field === 'divisionId') {
      this.getDivisionsAndShow(step, loadingMessageIndex);
    } else if (step.field === 'districtId') {
      const divisionId = this.formData['divisionId'];
      if (divisionId) {
        this.getDistrictByDivisionsAndShow(divisionId, step, loadingMessageIndex);
      }
    } else if (step.field === 'tehsilId') {
      const districtId = this.formData['districtId'];
      if (districtId) {
        this.getTehsilByDistrictAndShow(districtId, step, loadingMessageIndex);
      }
    } else if (step.field === 'mouzaId') {
      const tehsilId = this.formData['tehsilId'];
      if (tehsilId) {
        this.getMouzaByTehsilAndShow(tehsilId, step, loadingMessageIndex);
      }
    }
  }

  private showDropdownOptions(step: any, dataList: any[], textField: string, valueField: string, loadingMessageIndex?: number) {
    console.log(`Showing dropdown options for ${step.field}:`, dataList); // Debug log

    // Filter out "Not Available" entries and invalid data
    const filteredDataList = dataList.filter(item => {
      const text = item[textField];
      const value = item[valueField];

      // Filter out entries with "Not Available", null, undefined, or empty values
      return text &&
        value &&
        text.toString().toLowerCase() !== 'not available' &&
        text.toString().toLowerCase() !== 'n/a' &&
        text.toString().trim() !== '' &&
        value.toString().trim() !== '';
    });

    console.log(`Filtered data for ${step.field}:`, filteredDataList); // Debug log

    const options = filteredDataList.map(item => ({
      text: item[textField],
      value: item[valueField]
    }));

    console.log(`Mapped options for ${step.field}:`, options); // Debug log

    if (options.length === 0) {
      console.log(`No valid options available for ${step.field}`); // Debug log

      // Remove loading message if provided
      if (loadingMessageIndex !== undefined) {
        this.messages.splice(loadingMessageIndex, 1);
      }

      const noOptionsMessage: Message = {
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId(),
        isStreaming: true
      };

      noOptionsMessage.text = 'CHATBOT_NO_OPTIONS';
      noOptionsMessage.isStreaming = false;
      this.messages.push(noOptionsMessage);

      this.currentStep++;
      setTimeout(() => this.continueGuidedForm(), 500);
      return;
    }

    // Remove loading message if provided
    if (loadingMessageIndex !== undefined) {
      this.messages.splice(loadingMessageIndex, 1);
    }

    // Check if this is an optional field with many options (use dropdown)
    const useDropdown = !step.required && options.length > 5;

    // Create selection message
    const selectionMessage: Message = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId(),
      isStreaming: true,
      currentField: step.field
    };

    if (useDropdown) {
      // Use dropdown for optional fields with many options
      selectionMessage.showDropdown = true;
      selectionMessage.dropdownOptions = options;
      selectionMessage.allowSkip = !step.required;
    } else {
      // Use button options for required fields or fields with few options
      selectionMessage.showOptions = true;
      options.push({ text: 'CHATBOT_BACK_TO_MAIN', value: 'back_to_main' });
      selectionMessage.options = options;
    }

    if (useDropdown) {
      selectionMessage.showButtons = true;
      selectionMessage.buttons = [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }];
    }

    this.messages.push(selectionMessage);
    const messageIndex = this.messages.length - 1;

    // Show the selection text immediately
    const fieldLabel = this.translate.instant(step.label);
    const selectPrompt = this.translate.instant('CHATBOT_SELECT_FIELD', { field: fieldLabel });
    this.messages[messageIndex].text = selectPrompt;
    this.messages[messageIndex].isStreaming = false;
    // setTimeout(() => this.scrollToBottom(), 100);
  }

  private askForYesNoSelection(step: any) {
    let questionText = '';
    if (step.field === 'isAnonymous') {
      questionText = 'WOULD_YOU_LIKE_ANONYMOUS';
    } else if (step.field === 'documentAttachment') {
      questionText = 'DO_YOU_WANT_ATTACH_DOCUMENT';
    } else {
      questionText = step.label; // Assumes step label is a key
    }

    const yesNoMessage: Message = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId(),
      showOptions: false,
      options: [
        { text: 'CHATBOT_YES', value: true },
        { text: 'CHATBOT_NO', value: false },
        { text: 'CHATBOT_BACK_TO_MAIN', value: 'back_to_main' }
      ],
      currentField: step.field,
      isStreaming: true
    };

    this.messages.push(yesNoMessage);
    const messageIndex = this.messages.length - 1;

    // Show the question text and options immediately
    this.messages[messageIndex].text = questionText;
    this.messages[messageIndex].isStreaming = false;
    this.messages[messageIndex].showOptions = true;
    // setTimeout(() => this.scrollToBottom(), 100);
  }

  private askForTextInput(step: any) {
    const textInputMessage: Message = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId(),
      currentField: step.field,
      isStreaming: true
    };

    this.messages.push(textInputMessage);
    const messageIndex = this.messages.length - 1;

    // Show the text input prompt immediately with skip option if not required
    const fieldLabel = this.translate.instant(step.label);
    let promptText = '';

    if (step.required) {
      promptText = this.translate.instant('CHATBOT_ENTER_FIELD', { field: fieldLabel });
    } else {
      promptText = this.translate.instant('CHATBOT_OPTIONAL_PROMPT_GENERIC', { field: fieldLabel });
    }

    // Append max length info if defined
    // if (step.maxLength) {
    //   promptText += ' ' + this.translate.instant('CHATBOT_MAX_LENGTH_INFO', { max: step.maxLength });
    // }

    this.messages[messageIndex].text = promptText;
    this.messages[messageIndex].isStreaming = false;

    // Show Back to Main Menu button, and Skip button if optional
    this.messages[messageIndex].showButtons = true;
    const buttons = [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }];
    if (!step.required) {
      buttons.unshift({ text: 'CHATBOT_SKIP', action: 'skip_field' });
    }
    this.messages[messageIndex].buttons = buttons;
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private fillFormField(fieldName: string, value: any) {
    console.log('Filling form field:', fieldName, 'with value:', value); // Debug log

    // First show visual feedback in form with lazy loading animation
    // Then update the reactive form after animation completes
    this.animateFormFieldFilling(fieldName, value);
  }

  private animateFormFieldFilling(fieldName: string, value: any) {
    setTimeout(() => {
      const formElement = document.querySelector(`[formControlName="${fieldName}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

      if (formElement) {
        const scrollTarget = this.getScrollTargetElement(fieldName, formElement);
        this.scrollElementIntoComfortableView(scrollTarget);

        // Add loading class for visual feedback
        formElement.classList.add('chatbot-filling');

        if (formElement.tagName === 'SELECT') {
          this.animateSelectField(formElement as HTMLSelectElement, value, fieldName);
        } else if (formElement.type === 'checkbox') {
          this.animateCheckboxField(formElement as HTMLInputElement, value, fieldName);
        } else {
          // For input and textarea elements, get the placeholder
          const inputElement = formElement as HTMLInputElement | HTMLTextAreaElement;
          const originalPlaceholder = inputElement.placeholder || '';
          this.animateTextField(inputElement, value.toString(), originalPlaceholder, fieldName, value);
        }
      } else {
        // No DOM element found (e.g. citizen detail fields mapped to different form control names).
        // Emit directly so the parent component can handle the field name mapping.
        this.formFieldUpdate.emit({ field: fieldName, value: value });
      }
    }, 100);
  }

  private getScrollTargetElement(
    fieldName: string,
    formElement: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ): Element {
    if (formElement instanceof HTMLInputElement && formElement.type === 'hidden') {
      const labelElement = document.querySelector(`label[for="${fieldName}"]`);
      if (labelElement) {
        return labelElement.closest('.form-group') || labelElement;
      }

      return (
        formElement.closest('.form-group') ||
        formElement.closest('.col-12, .col-lg-4, .col-md-6, .col-md-2') ||
        formElement
      );
    }

    if (formElement instanceof HTMLInputElement && formElement.type === 'checkbox') {
      return formElement.closest('label') || formElement;
    }

    return formElement;
  }

  private scrollElementIntoComfortableView(element: Element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const absoluteTop = rect.top + window.scrollY;

    // Only intervene when the element is actually hard to see:
    // 1. it is above the viewport, or
    // 2. it is too low and likely to be obscured by the floating chatbot.
    const topSafeZone = 0;
    const bottomSafeZone = Math.min(260, Math.floor(viewportHeight * 0.32));

    if (rect.top < topSafeZone) {
      window.scrollTo({
        top: Math.max(0, absoluteTop - 40),
        behavior: 'smooth',
      });
      return;
    }

    if (rect.bottom > viewportHeight - bottomSafeZone) {
      window.scrollTo({
        top: Math.max(0, absoluteTop - 120),
        behavior: 'smooth',
      });
    }
  }

  private animateTextField(element: HTMLInputElement | HTMLTextAreaElement, finalValue: string, originalPlaceholder: string, fieldName: string, value: any) {
    // Show loading state for visual feedback
    element.placeholder = `${this.translate.instant('LOADING')}...`;
    element.classList.add('chatbot-loading');

    setTimeout(() => {
      element.placeholder = originalPlaceholder;
      element.classList.remove('chatbot-loading');

      // Emit update immediately without internal word-by-word animation
      // This allows the parent component to handle char-by-char animation
      this.formFieldUpdate.emit({ field: fieldName, value: value });

      // Still show the 'filled' state after a delay
      setTimeout(() => {
        element.classList.add('chatbot-filled');
        setTimeout(() => {
          element.classList.remove('chatbot-filled');
        }, 1000);
      }, 500);

    }, 800); // UI loading delay
  }

  private animateSelectField(element: HTMLSelectElement, value: any, fieldName: string) {
    // Show loading state
    const originalHTML = element.innerHTML;
    element.innerHTML = `<option>${this.translate.instant('LOADING')}...</option>`;
    element.classList.add('chatbot-loading');

    setTimeout(() => {
      element.innerHTML = originalHTML;
      element.classList.remove('chatbot-loading');

      // Emit update directly
      this.formFieldUpdate.emit({ field: fieldName, value: value });

      // Show filled state
      element.classList.add('chatbot-filled');
      setTimeout(() => {
        element.classList.remove('chatbot-filled');
      }, 1000);

    }, 800);
  }

  private animateCheckboxField(element: HTMLInputElement, value: boolean, fieldName: string) {
    element.classList.add('chatbot-loading');

    setTimeout(() => {
      element.classList.remove('chatbot-loading');

      // Emit update
      this.formFieldUpdate.emit({ field: fieldName, value: value });

      element.classList.add('chatbot-filled');
      setTimeout(() => {
        element.classList.remove('chatbot-filled');
      }, 1000);
    }, 500);
  }




  private completeGuidedForm() {
    this.showSubmitStep();
  }

  showSubmitStep() {
    this.isGuidedMode = false;
    this.showDocumentUpload = false;
    this.isTyping = true;
    this.scrollToBottom();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        text: 'CHATBOT_FORM_COMPLETED',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId()
      });
      this.scrollToBottom();
      this.showSubmitButton = true;

      // Scroll to the submit button area in the form if possible
      setTimeout(() => {
        const submitBtn = document.querySelector('.submit-complaint-btn') ||
          document.querySelector('button[type="submit"]') ||
          document.querySelector('.submit-container');
        if (submitBtn) {
          submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }, 1000);
  }

  // API Methods for dynamic data


  private getComplaintCategoryBySegmentAndShow(id: number, step: any, loadingMessageIndex: number) {
    // Check cache first
    if (this.dataCache.categories && this.dataCache.categories.length > 0) {
      console.log('Using cached categories data');
      this.categoryList = this.dataCache.categories;
      this.showDropdownOptions(step, this.categoryList, 'name', 'complaintCategoryId', loadingMessageIndex);
      return;
    }

    // Check if already loading to prevent duplicate calls
    if (this.loadingFlags.categories) {
      console.log('Categories already loading, skipping duplicate call');
      return;
    }

    const apiKey = `categories_${id}`;

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey)) {
      console.log('Categories API call already active, skipping duplicate call');
      return;
    }

    // Set loading flag immediately to prevent race conditions
    this.loadingFlags.categories = true;
    this.activeApiCalls.add(apiKey);

    // Try to get data from parent component first
    this.formFieldUpdate.emit({ field: 'getCategoryList', value: true });

    // Wait longer for parent to provide data
    setTimeout(() => {
      if (this.categoryList && this.categoryList.length > 0) {
        console.log('Using categories data from parent component');
        this.dataCache.categories = [...this.categoryList];
        this.loadingFlags.categories = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.categoryList, 'name', 'complaintCategoryId', loadingMessageIndex);
        return;
      }

      // Only make API call if parent didn't provide data
      console.log('Parent did not provide categories, making API call for segment:', id);

      this.applicationDataService.getAllComplaintCategoriesBySegment(id).subscribe({
        next: (res: any) => {
          console.log('Categories loaded via API:', res?.data);
          this.categoryList = res?.data || [];
          this.dataCache.categories = [...this.categoryList];
          this.loadingFlags.categories = false;
          this.activeApiCalls.delete(apiKey);
          this.showDropdownOptions(step, this.categoryList, 'name', 'complaintCategoryId', loadingMessageIndex);
        },
        error: (err: any) => {
          console.error('Error loading categories:', err);
          this.loadingFlags.categories = false;
          this.activeApiCalls.delete(apiKey);

          // Remove loading message and show error
          this.messages.splice(loadingMessageIndex, 1);
          const errorMessage: Message = {
            text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId(),
            isStreaming: false
          };
          this.messages.push(errorMessage);
        }
      });
    }, 500); // Increased timeout to 500ms for better parent-child communication
  }



  private getAllComplaintByParentCategoryAndShow(id: number, step: any, loadingMessageIndex: number) {
    const apiKey = `subcategories_${id}`;

    // Check cache first
    if (this.dataCache.subcategories.has(id)) {
      const cachedData = this.dataCache.subcategories.get(id) || [];
      if (cachedData.length > 0) {
        console.log('Using cached subcategories data for category:', id);
        this.subCategoryList = cachedData;
        this.showDropdownOptions(step, this.subCategoryList, 'name', 'complaintCategoryId', loadingMessageIndex);
        return;
      }
    }

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey) || this.loadingFlags.subcategories) {
      console.log('Subcategories already loading, skipping duplicate call');
      return;
    }

    this.loadingFlags.subcategories = true;
    this.activeApiCalls.add(apiKey);
    console.log('Loading subcategories for category ID:', id);

    this.applicationDataService.getAllComplaintByParentCategory(id).subscribe({
      next: (res: any) => {
        console.log('Subcategories loaded:', res?.data);
        this.subCategoryList = res?.data || [];
        this.dataCache.subcategories.set(id, this.subCategoryList);
        this.loadingFlags.subcategories = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.subCategoryList, 'name', 'complaintCategoryId', loadingMessageIndex);
      },
      error: (err: any) => {
        console.error('Error loading subcategories:', err);
        this.loadingFlags.subcategories = false;
        this.activeApiCalls.delete(apiKey);

        // Remove loading message and show error
        this.messages.splice(loadingMessageIndex, 1);
        const errorMessage: Message = {
          text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          isStreaming: false
        };
        this.messages.push(errorMessage);
      }
    });
  }



  private getDivisionsAndShow(step: any, loadingMessageIndex: number) {
    // Check cache first
    if (this.dataCache.divisions && this.dataCache.divisions.length > 0) {
      console.log('Using cached divisions data');
      this.divisionList = this.dataCache.divisions;
      this.showDropdownOptions(step, this.divisionList, 'name', 'adminStrId', loadingMessageIndex);
      return;
    }

    if (this.loadingFlags.divisions) {
      console.log('Divisions already loading, skipping duplicate call');
      return;
    }

    const apiKey = `divisions_${this.divisionIds.join('_')}`;

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey)) {
      console.log('Divisions API call already active, skipping duplicate call');
      return;
    }

    // Set loading flag immediately to prevent race conditions
    this.loadingFlags.divisions = true;
    this.activeApiCalls.add(apiKey);

    // Try to get data from parent component first
    this.formFieldUpdate.emit({ field: 'getDivisionList', value: true });

    // Wait for parent to provide data
    setTimeout(() => {
      if (this.divisionList && this.divisionList.length > 0) {
        console.log('Using divisions data from parent component');
        this.dataCache.divisions = [...this.divisionList];
        this.loadingFlags.divisions = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.divisionList, 'name', 'adminStrId', loadingMessageIndex);
        return;
      }

      // Only make API call if parent didn't provide data
      console.log('Parent did not provide divisions, making API call for IDs:', this.divisionIds);

      this.applicationDataService.getAdminStructureByIds(this.divisionIds).subscribe({
        next: (res: any) => {
          console.log('Divisions loaded via API:', res?.data);
          this.divisionList = res?.data || [];
          this.dataCache.divisions = [...this.divisionList];
          this.loadingFlags.divisions = false;
          this.activeApiCalls.delete(apiKey);
          this.showDropdownOptions(step, this.divisionList, 'name', 'adminStrId', loadingMessageIndex);
        },
        error: (err: any) => {
          console.error('Error loading divisions:', err);
          this.loadingFlags.divisions = false;
          this.activeApiCalls.delete(apiKey);

          // Remove loading message and show error
          this.messages.splice(loadingMessageIndex, 1);
          const errorMessage: Message = {
            text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId(),
            isStreaming: false
          };
          this.messages.push(errorMessage);
        }
      });
    }, 500); // Increased timeout for better parent-child communication
  }



  private getDistrictByDivisionsAndShow(id: number, step: any, loadingMessageIndex: number) {
    const apiKey = `districts_${id}`;

    // Check cache first
    if (this.dataCache.districts.has(id)) {
      const cachedData = this.dataCache.districts.get(id) || [];
      if (cachedData.length > 0) {
        console.log('Using cached districts data for division:', id);
        this.districtList = cachedData;
        this.showDropdownOptions(step, this.districtList, 'name', 'adminStrId', loadingMessageIndex);
        return;
      }
    }

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey) || this.loadingFlags.districts) {
      console.log('Districts already loading, skipping duplicate call');
      return;
    }

    this.loadingFlags.districts = true;
    this.activeApiCalls.add(apiKey);
    console.log('Loading districts for division ID:', id);

    this.applicationDataService.getAdmStructurebyParentId(id).subscribe({
      next: (res: any) => {
        console.log('Districts loaded:', res?.data);
        this.districtList = res?.data || [];
        this.dataCache.districts.set(id, this.districtList);
        this.loadingFlags.districts = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.districtList, 'name', 'adminStrId', loadingMessageIndex);
      },
      error: (err: any) => {
        console.error('Error loading districts:', err);
        this.loadingFlags.districts = false;
        this.activeApiCalls.delete(apiKey);

        // Remove loading message and show error
        this.messages.splice(loadingMessageIndex, 1);
        const errorMessage: Message = {
          text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          isStreaming: false
        };
        this.messages.push(errorMessage);
      }
    });
  }





  private getTehsilByDistrictAndShow(id: number, step: any, loadingMessageIndex: number) {
    const apiKey = `tehsils_${id}`;

    // Check cache first
    if (this.dataCache.tehsils.has(id)) {
      const cachedData = this.dataCache.tehsils.get(id) || [];
      if (cachedData.length > 0) {
        console.log('Using cached tehsils data for district:', id);
        this.tehsilList = cachedData;
        this.showDropdownOptions(step, this.tehsilList, 'name', 'adminStrId', loadingMessageIndex);
        return;
      }
    }

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey) || this.loadingFlags.tehsils) {
      console.log('Tehsils already loading, skipping duplicate call');
      return;
    }

    this.loadingFlags.tehsils = true;
    this.activeApiCalls.add(apiKey);
    console.log('Loading tehsils for district ID:', id);

    this.applicationDataService.getAdmStructurebyParentId(id).subscribe({
      next: (res: any) => {
        console.log('Tehsils loaded:', res?.data);
        this.tehsilList = res?.data || [];
        this.dataCache.tehsils.set(id, this.tehsilList);
        this.loadingFlags.tehsils = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.tehsilList, 'name', 'adminStrId', loadingMessageIndex);
      },
      error: (err: any) => {
        console.error('Error loading tehsils:', err);
        this.loadingFlags.tehsils = false;
        this.activeApiCalls.delete(apiKey);

        // Remove loading message and show error
        this.messages.splice(loadingMessageIndex, 1);
        const errorMessage: Message = {
          text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          isStreaming: false
        };
        this.messages.push(errorMessage);
      }
    });
  }

  private getMouzaByTehsilAndShow(id: number, step: any, loadingMessageIndex: number) {
    const apiKey = `mouzas_${id}`;

    // Check cache first
    if (this.dataCache.mouzas.has(id)) {
      const cachedData = this.dataCache.mouzas.get(id) || [];
      if (cachedData.length > 0) {
        console.log('Using cached mouzas data for tehsil:', id);
        this.mouzaList = cachedData;
        this.showDropdownOptions(step, this.mouzaList, 'name', 'adminStrId', loadingMessageIndex);
        return;
      }
    }

    // Check if API call is already active
    if (this.activeApiCalls.has(apiKey) || this.loadingFlags.mouzas) {
      console.log('Mouzas already loading, skipping duplicate call');
      return;
    }

    this.loadingFlags.mouzas = true;
    this.activeApiCalls.add(apiKey);
    console.log('Loading mouzas for tehsil ID:', id);

    this.applicationDataService.getAdmStructurebyParentId(id).subscribe({
      next: (res: any) => {
        console.log('Mouzas loaded:', res?.data);
        this.mouzaList = res?.data || [];
        this.dataCache.mouzas.set(id, this.mouzaList);
        this.loadingFlags.mouzas = false;
        this.activeApiCalls.delete(apiKey);
        this.showDropdownOptions(step, this.mouzaList, 'name', 'adminStrId', loadingMessageIndex);
      },
      error: (err: any) => {
        console.error('Error loading mouzas:', err);
        this.loadingFlags.mouzas = false;
        this.activeApiCalls.delete(apiKey);

        // Remove loading message and show error
        this.messages.splice(loadingMessageIndex, 1);
        const errorMessage: Message = {
          text: this.translate.instant('CHATBOT_ERROR_MESSAGE'),
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          isStreaming: false
        };
        this.messages.push(errorMessage);
      }
    });
  }

  // Skip document upload and move to submit
  skipDocumentUpload() {
    this.showDocumentUpload = false;

    // Add message to chat
    this.messages.push({
      text: this.translate.instant('CHATBOT_SKIP'),
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    if (this.documentList.length > 0) {
      this.messages.push({
        text: `${this.translate.instant('TOTAL')} ${this.documentList.length} ${this.translate.instant('CHATBOT_DOCUMENT_ATTACHED')}.`,
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId()
      });
    } else {
      this.messages.push({
        text: this.translate.instant('CHATBOT_NO_DOCUMENT'),
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId()
      });
    }

    setTimeout(() => {
      this.showSubmitStep();
    }, 500);
  }

  // Document upload methods
  onDocumentSelect(event: any) {
    const fileToUpload = event?.target.files.item(0);
    if (fileToUpload) {
      const allowedTypes = [
        'image/png',
        'image/jpg',
        'image/jpeg',
        'image/gif',
        'application/pdf',
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes

      if (allowedTypes.includes(fileToUpload.type) && fileToUpload.size <= maxSize) {
        this.fileError = false;
        const formdata: FormData = new FormData();
        formdata.append('isPublished', 'true');
        formdata.append('file', fileToUpload);

        this.messages.push({
          text: 'LOADING',
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId()
        });

        this.applicationDataService.addDocumentService(formdata).subscribe({
          next: (res: any) => {
            this.documentList.push(res?.data);

            // Remove uploading message
            this.messages.pop();

            this.messages.push({
              text: `${this.translate.instant('CHATBOT_DOCUMENT_UPLOADED')}: ${fileToUpload.name}\n\n${this.translate.instant('TOTAL')} ${this.documentList.length} ${this.translate.instant('CHATBOT_DOCUMENT_ATTACHED')}`,
              sender: 'bot',
              timestamp: new Date(),
              id: this.generateId()
            });

            // Update form with document IDs
            const documentIds = this.documentList.map(doc => doc?.complaintDocumentId);
            this.fillFormField('complaintDocumentIds', documentIds);

            // Also emit the uploaded document to parent component
            this.formFieldUpdate.emit({ field: 'uploadedDocument', value: res?.data });

            // Show message to upload more or skip
            this.messages.push({
              text: 'DO_YOU_WANT_ATTACH_DOCUMENT',
              sender: 'bot',
              timestamp: new Date(),
              id: this.generateId()
            });

            // Keep document upload section visible for more uploads
            this.showDocumentUpload = true;
            setTimeout(() => this.scrollToBottom(), 100);
          },
          error: (err: any) => {
            console.error('Document upload error:', err);
            // Remove uploading message
            this.messages.pop();

            this.messages.push({
              text: 'CHATBOT_ERROR_MESSAGE',
              sender: 'bot',
              timestamp: new Date(),
              id: this.generateId()
            });
          }
        });
      } else {
        this.fileError = true;
        this.messages.push({
          text: 'CHATBOT_INVALID_FILE',
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId()
        });
      }
      event.target.value = null;
    }
  }

  // Submit complaint
  onSubmitComplaint() {
    console.log('Submit complaint button clicked in chatbot'); // Debug log

    this.messages.push({
      text: 'CHATBOT_SUBMIT_COMPLAINT',
      sender: 'user',
      timestamp: new Date(),
      id: this.generateId()
    });

    this.messages.push({
      text: 'LOADING',
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Trigger form submission in parent component
    console.log('Emitting submitForm event to parent component'); // Debug log
    this.formFieldUpdate.emit({ field: 'submitForm', value: true });

    this.showSubmitButton = false;
    // setTimeout(() => this.scrollToBottom(), 100);
  }

  public getConversationDetails(): string {
    return this.messages
      .filter((message) => message.text && !message.isLoading)
      .map((message) => {
        const label = message.sender === 'bot' ? 'Bot' : 'User';
        const text = this.translate.instant(message.text);
        return `[${label}]: ${text}`;
      })
      .join('\n');
  }

  // Method to show submission success message (can be called from parent)
  showSubmissionSuccess(complaintNo?: string) {
    // Remove the "submitting" message
    this.messages.pop();

    const successMessage = complaintNo
      ? `${this.translate.instant('CHATBOT_SUBMIT_SUCCESS')}! ${this.translate.instant('COMPLAINT_NO')}: ${complaintNo}`
      : this.translate.instant('CHATBOT_SUBMIT_SUCCESS');

    this.messages.push({
      text: successMessage,
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Reset guided-form state
    this.currentStep = 0;
    this.formData = {};
    this.isGuidedMode = false;
    this.showSubmitButton = false;
    this.showDocumentUpload = false;
    this.documentList = [];
    this.chatMode = 'general';

    // Return to main menu after a short pause so the user can read the success message
    setTimeout(() => {
      this.showMainMenu(true);
    }, 2500);
  }

  // Method to show submission error (can be called from parent)
  showSubmissionError(error?: string) {
    // Remove the "submitting" message
    this.messages.pop();

    const errorMessage = error
      ? `${this.translate.instant('CHATBOT_SUBMIT_ERROR')}: ${error}`
      : 'CHATBOT_SUBMIT_ERROR_GENERAL';

    this.messages.push({
      text: errorMessage,
      sender: 'bot',
      timestamp: new Date(),
      id: this.generateId()
    });

    // Show submit button again for retry
    this.showSubmitButton = true;
    // setTimeout(() => this.scrollToBottom(), 100);
  }

  // Handle Get Complaint Status action
  private handleGetComplaintStatus() {
    this.isTyping = true;
    this.scrollToBottom();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        text: 'CHATBOT_ENTER_COMPLAINT_NO',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId(),
        showButtons: true,
        buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
      });
      this.scrollToBottom();
    }, 1000);
  }

  // Handle Question/Answer action
  private handleQuestionAnswer() {
    this.isTyping = true;
    this.scrollToBottom();

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        text: 'CHATBOT_ASK_QUESTION_PROMPT',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId(),
        showButtons: true,
        buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
      });
      this.scrollToBottom();
    }, 1000);
  }

  // Method to receive data from parent component to avoid duplicate API calls
  receiveDataFromParent(dataType: string, data: any[]) {
    switch (dataType) {
      case 'categories':
        if (data && data.length > 0) {
          this.categoryList = [...data];
          this.dataCache.categories = [...data];
          console.log('Received categories from parent:', data.length, 'items');
        }
        break;
      case 'divisions':
        if (data && data.length > 0) {
          this.divisionList = [...data];
          this.dataCache.divisions = [...data];
          console.log('Received divisions from parent:', data.length, 'items');
        }
        break;
      default:
        console.log('Unknown data type received from parent:', dataType);
    }
  }

  // Handle Status Check Logic
  private handleStatusCheck(complaintId: string) {
    // Relaxed pattern to allow 4 to 6 digits at the end to accommodate various test cases
    if (!CHATBOT_TRACKING_ID_PATTERN.test(complaintId)) {
      this.isTyping = false;
      this.isSending = false;
      this.messages.push({
        text: 'CHATBOT_INVALID_TRACKING_ID',
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId(),
        showButtons: true,
        buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
      });
      // Keeping chatMode as 'status' so user can try again immediately
      return;
    }

    // Store complaint number for later use
    this.currentComplaintNum = complaintId;

    if (this.tokenStorage.isAuthenticated()) {
      const rawMap = sessionStorage.getItem('userComplaints');
      if (rawMap) {
        try {
          const complaintMap = JSON.parse(rawMap);
          const complaint = complaintMap[complaintId];

          if (complaint) {
            // Complaint belongs to this logged-in user — skip OTP entirely
            this.isTyping = true;
            this.scrollToBottom();

            setTimeout(() => {
              this.isTyping = false;
              const htmlContent = this.formatComplaintStatusHtml(complaint);
              this.messages.push({
                text: '',
                html: htmlContent,
                sender: 'bot',
                timestamp: new Date(),
                id: this.generateId(),
                showButtons: true,
                buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
              });
              // Stay in status mode so the user can look up another complaint
              this.chatMode = 'status';
              this.scrollToBottom();
            }, 1000);

            return; // ← exit early; OTP flow is not needed
          }
        } catch {
          // Malformed sessionStorage value — fall through to the standard OTP flow
        }
      }
    }

    this.isTyping = true;
    this.scrollToBottom();

    // Call the API to send OTP
    this.applicationDataService.getComplaintStatus(complaintId).subscribe({
      next: (response: any) => {
        setTimeout(() => {
          this.isTyping = false;

          const otpMsgId = this.generateId();
          this.otpMessageId = otpMsgId;

          this.messages.push({
            text: 'CHATBOT_OTP_SENT',
            sender: 'bot',
            timestamp: new Date(),
            id: otpMsgId
          });

          // Start 2 minute timer
          this.startOTPTimer();

          // Change mode to wait for OTP input
          this.chatMode = 'otp_input_wait';
          this.scrollToBottom();
        }, 1000);
      },
      error: (err: any) => {
        console.error('Status API error:', err);
        setTimeout(() => {
          this.isTyping = false;

          this.messages.push({
            text: 'CHATBOT_OTP_SEND_ERROR',
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId(),
            showButtons: true,
            buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
          });
          this.scrollToBottom();
        }, 1000);
      }
    });
  }

  private startOTPTimer() {
    this.clearTimer();
    this.timeLeft = 120; // 2 minutes in seconds

    const timerMessageId = this.generateId();

    // Add timer message
    this.messages.push({
      text: `${this.translate.instant('CHATBOT_TIME_REMAINING')}: ${formatTimer(this.timeLeft)}`,
      sender: 'bot',
      timestamp: new Date(),
      id: timerMessageId
    });

    this.timerIntervalId = setInterval(() => {
      this.timeLeft--;

      // Update timer message
      const msgIndex = this.messages.findIndex(m => m.id === timerMessageId);
      if (msgIndex !== -1) {
        this.messages[msgIndex].text = `${this.translate.instant('CHATBOT_TIME_REMAINING')}: ${formatTimer(this.timeLeft)}`;
      }

      if (this.timeLeft <= 0) {
        this.clearTimer();

        // Update timer message to show it expired
        if (msgIndex !== -1) {
          this.messages[msgIndex].text = this.translate.instant('CHATBOT_OTP_EXPIRED');
        }

        // Show retry option as a new message at the end of chat
        this.messages.push({
          text: 'PLEASE_CHOOSE_OPTION',
          sender: 'bot',
          timestamp: new Date(),
          id: this.generateId(),
          showButtons: true,
          buttons: [
            { text: 'CHATBOT_RETRY_OTP', action: 'retry_otp' },
            { text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }
          ]
        });

        this.chatMode = 'otp_expired';
        setTimeout(() => this.scrollToBottom(), 100);
      }
    }, 1000);
  }

  private clearTimer() {
    if (this.timerIntervalId) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  private verifyOTP(otp: string) {
    this.isTyping = true;
    this.scrollToBottom();

    this.applicationDataService.verifyOTP(this.currentComplaintNum, otp).subscribe({
      next: (res: any) => {
        setTimeout(() => {
          this.isTyping = false;
          this.clearTimer(); // Stop timer immediately on success

          let htmlContent: SafeHtml | undefined;
          let textContent = '';

          if (res && res.data && res.data.data && res.data.data.length > 0) {
            const details = res.data.data[0];
            htmlContent = this.formatComplaintStatusHtml(details);
          } else {
            textContent = extractBotResponse(res) || this.translate.instant('CHATBOT_NO_DETAILS_FOUND');
          }

          this.messages.push({
            text: textContent,
            html: htmlContent,
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId(),
            showButtons: true,
            buttons: [{ text: 'CHATBOT_BACK_TO_MAIN', action: 'back_to_main' }]
          });

          this.chatMode = 'status'; // Keep in status mode for sequential checks
          this.scrollToBottom();
        }, 1000);
      },
      error: (err: any) => {
        setTimeout(() => {
          this.isTyping = false;
          console.error('OTP Verify error', err);

          let errorMessage = this.translate.instant('CHATBOT_OTP_FAILED');
          if (err.error && err.error.message) {
            errorMessage = err.error.message;
          }

          const isTimerRunning = this.timeLeft > 0 && this.chatMode !== 'otp_expired';

          this.messages.push({
            text: isTimerRunning ? this.translate.instant('CHATBOT_ENTER_VALID_OTP') : errorMessage,
            sender: 'bot',
            timestamp: new Date(),
            id: this.generateId(),
            showButtons: !isTimerRunning,
            buttons: isTimerRunning ? [] : [
              { text: this.translate.instant('CHATBOT_RETRY_OTP'), action: 'retry_otp' },
              { text: this.translate.instant('CHATBOT_BACK_TO_MAIN'), action: 'back_to_main' }
            ]
          });

          this.scrollToBottom();
        }, 1000);
      }
    });
  }

  private formatComplaintStatusHtml(details: any): SafeHtml {
    const tableStyle = `
      width: 100%; 
      border-collapse: separate; 
      border-spacing: 0; 
      border: 1px solid #e0e0e0; 
      border-radius: 8px; 
      overflow: hidden;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 14px;
      margin-top: 8px;
    `;

    const headerStyle = `
      background-color: #198754; 
      color: white; 
      padding: 12px 16px; 
      text-align: left; 
      font-weight: 600;
      font-size: 16px;
    `;

    const rowStyle = `border-bottom: 1px solid #f0f0f0;`;
    const labelStyle = `padding: 10px 16px; font-weight: 600; color: #333; background-color: #f9f9f9; width: 40%; vertical-align: top; border-bottom: 1px solid #e0e0e0;`;
    const valueStyle = `padding: 10px 16px; color: #555; width: 60%; vertical-align: top; border-bottom: 1px solid #e0e0e0;`;

    // Extract fields
    const complaintNo = details.complaintNo || this.translate.instant('CHATBOT_NOT_AVAILABLE');
    const complainantName = details.complainantName || this.translate.instant('CHATBOT_NOT_AVAILABLE');
    const cnic = details.cnic || this.translate.instant('CHATBOT_NOT_AVAILABLE');
    const cellPhone = details.cellPhoneNo || this.translate.instant('CHATBOT_NOT_AVAILABLE');
    const status = details.currentProcess?.name || this.translate.instant('CHATBOT_UNKNOWN_STATUS');
    // Remarks could be added if available in the object, currently user JSON showed none in specific fields, maybe 'comment'? 
    // JSON has 'comment': null. Let's stick to the visible fields in user image + Status.

    const html = `
      <div style="${tableStyle}">
        <div style="${headerStyle}">${this.translate.instant('COMPLAINT_DETAILS')}</div>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="${rowStyle}">
            <td style="${labelStyle}">${this.translate.instant('COMPLAINT_NO')}</td>
            <td style="${valueStyle}">${complaintNo}</td>
          </tr>
          <tr style="${rowStyle}">
            <td style="${labelStyle}">${this.translate.instant('COMPLAINANT_NAME')}</td>
            <td style="${valueStyle}">${complainantName}</td>
          </tr>
          <tr style="${rowStyle}">
            <td style="${labelStyle}">${this.translate.instant('CNIC')}</td>
            <td style="${valueStyle}">${cnic}</td>
          </tr>
          <tr style="${rowStyle}">
            <td style="${labelStyle}">${this.translate.instant('PHONE_NUMBER')}</td>
            <td style="${valueStyle}">${cellPhone}</td>
          </tr>
          <tr>
            <td style="${labelStyle.replace('border-bottom: 1px solid #e0e0e0;', '')}">${this.translate.instant('STATUS')}</td>
            <td style="${valueStyle.replace('border-bottom: 1px solid #e0e0e0;', '')}">${status}</td>
          </tr>
        </table>
      </div>
    `;

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // Update buttons on the existing welcome message without adding a new message
  private updateMainMenuButtons() {
    let buttons = [
      { text: 'CHATBOT_ASK_QUESTION', action: 'question_answer' }
    ];

    if (this.tokenStorage.isAuthenticated()) {
      buttons.unshift({ text: 'CHATBOT_REGISTER_COMPLAINT', action: 'start_guided_form' });
    }

    // Find the last welcome menu message and update its buttons
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].sender === 'bot' && this.messages[i].text === 'CHATBOT_WELCOME') {
        this.messages[i].buttons = buttons;
        this.messages[i].showButtons = true;
        return;
      }
    }

    // If no existing welcome message found (shouldn't happen), fall back to showMainMenu
    this.showMainMenu();
  }

  private resetChatStateOnly() {
    this.chatMode = 'general';
    this.isGuidedMode = false;
    this.currentStep = 0;
    this.currentComplaintNum = '';
    if (this.otpTimerSubscription) clearInterval(this.otpTimerSubscription);

    // Hide all interactive elements from previous messages
    this.messages.forEach(msg => {
      msg.showButtons = false;
      msg.showOptions = false;
      msg.showDropdown = false;
    });
  }

  private showMainMenu(force: boolean = false) {
    // Prevent duplicate welcome messages ONLY during automatic initialization
    if (!force) {
      const hasWelcome = this.messages.some(m => m.text === 'CHATBOT_WELCOME');
      if (hasWelcome || this.welcomePending) {
        if (hasWelcome) this.updateMainMenuButtons();
        return;
      }
    }

    this.welcomePending = true;
    this.resetChatStateOnly();
    this.isTyping = true;
    this.scrollToBottom();

    const welcomeText = 'CHATBOT_WELCOME';
    let buttons = [
      { text: 'CHATBOT_ASK_QUESTION', action: 'question_answer' }
    ];

    if (this.tokenStorage.isAuthenticated()) {
      buttons.unshift({ text: 'CHATBOT_REGISTER_COMPLAINT', action: 'start_guided_form' });
    }

    setTimeout(() => {
      this.isTyping = false;
      this.messages.push({
        text: welcomeText,
        sender: 'bot',
        timestamp: new Date(),
        id: this.generateId(),
        showButtons: true,
        buttons: buttons
      });

      this.welcomePending = false;
      this.scrollToBottom();
    }, 1000);
  }
}
